import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import anime from 'animejs/lib/anime.es.js';

import { FlightWatcherService } from 'app/services/flight-watcher-service';
import { UserService } from 'app/services/user.service';
import { PersistentWatcherService } from 'app/services/persistent-watcher.service';

import { WatcherCardComponent } from 'app/components/distinct-components/cards/watcher-card/watcher-card.component';
import { AddPersistentWatcherComponent } from './components/add-persistent-watcher/add-persistent-watcher.component';
import { PageWrapperComponent } from 'app/components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { EmptyStateCardComponent } from "app/components/distinct-components/cards/empty-state-card/empty-state-card.component";
import { PopTrayComponent } from 'app/components/ui-kit/popovers/pop-tray/pop-tray.component';
import { AlertMessageComponent } from 'app/components/ui-kit/alerts/alert-message/alert-message.component';
import { FlightOptionService } from 'app/services/flight-option-service';
import { Timestamp } from 'firebase/firestore';

@Component({
  selector: 'app-watchers',
  standalone: true,
  imports: [
    CommonModule, WatcherCardComponent,
    AddPersistentWatcherComponent,
    PageWrapperComponent, EmptyStateCardComponent,
    PopTrayComponent, AlertMessageComponent],
  templateUrl: './watchers.component.html',
  styleUrl: './watchers.component.scss'
})
export class WatchersComponent {
  isLoading: boolean = true;
  watchers: any[] = [];
  persistentWatchers: any[] = [];
  showAddPersistent: boolean = false;
  showStatus: boolean = false;
  showDeletePersistent: boolean = false;
  targetPersistentWatcher: any;
  statusActions: any;
  deletePersistentActions: any;
  targetWatcher: any;
  targetFlightOption: any;
  error: string | null = null;
  showDelete: boolean = false;
  showTogglePrivacy: boolean = false;
  makePrivateActions: any;
  makePublicActions: any;
  deleteActions: any;

  constructor(
    private watcherService: FlightWatcherService,
    private userService: UserService,
    private router: Router,
    private flightOptionService: FlightOptionService,
    private persistentWatcherService: PersistentWatcherService,
  ) {

    this.makePrivateActions = {
      primaryButtons: [
        {
          text: 'Make Private',
          function: () => this.handleTogglePrivacy(false),
        }
      ],
      secondaryButtons: [
        {
          text: 'Cancel',
          function: () => this.onClosePopTray(),
        }
      ],
      closeFunction: () => this.onClosePopTray()
    };

    this.makePublicActions = {
      primaryButtons: [
        {
          text: 'Make Public',
          function: () => this.handleTogglePrivacy(true),
        }
      ],
      secondaryButtons: [
        {
          text: 'Cancel',
          function: () => this.onClosePopTray(),
        }
      ],
      closeFunction: () => this.onClosePopTray()
    };

    this.deleteActions = {
      primaryButtons: [
        {
          text: 'Delete',
          function: () => this.handleDeleteWatcher(),
        }
      ],
      secondaryButtons: [
        {
          text: 'Cancel',
          function: () => this.onClosePopTray(),
        }
      ],
      closeFunction: () => this.onClosePopTray()
    };
    this.statusActions = {
      primaryButtons: [
        {
          text: 'Pause',
          function: () => this.handleTogglePersistentStatus(),
        }
      ],
      secondaryButtons: [
        { text: 'Cancel', function: () => this.onClosePopTray() }
      ],
      closeFunction: () => this.onClosePopTray()
    };

    this.deletePersistentActions = {
      primaryButtons: [
        { text: 'Delete', function: () => this.handleDeletePersistentWatcher() }
      ],
      secondaryButtons: [
        { text: 'Cancel', function: () => this.onClosePopTray() }
      ],
      closeFunction: () => this.onClosePopTray()
    };
  }

  ngOnInit(): void {
    this.loadWatcherData();
  }

  async loadWatcherData() {
    this.error = null;
    const user = await this.userService.getUserProfile();

    try {
      const watchers = await this.watcherService.getFlightWatchersByUserId(user.id);
      const now = Date.now();
      const validWatchers = watchers
        .filter(w => w.expiresOn && this.toMillis(w.expiresOn) > now)
        .sort((a, b) => this.toMillis(b.createdAt) - this.toMillis(a.createdAt));

      this.watchers = validWatchers;
      this.persistentWatchers = await this.persistentWatcherService.getWatchersByUserId(user.id);
    } catch (err) {
      console.error(err);
      this.error = 'Failed to load watchers';
    } finally {
      this.isLoading = false;
      setTimeout(() => {
        this.animatePage();
        this.animateResults();
      }, 100);
    }
  }

  private toMillis(date: Date | Timestamp): number {
    return date instanceof Date ? date.getTime() : date.toMillis();
  }

  animatePage() {
    anime({
      targets: '#page',
      duration: 650,
      opacity: [0, 1],
      translateY: [12, 0],
      easing: 'spring(1, 80, 10, 2)',
      loop: false,
    });
  }

  animateResults() {
    anime({
      targets: '#w-card',
      duration: 650,
      opacity: [0, 1],
      translateY: [12, 0],
      delay: anime.stagger(75),
      direction: 'alternate',
      easing: 'spring(1, 80, 10, 2)',
      loop: false,
    });
  }

  onShowAddPersistentWatcher() {
    this.showAddPersistent = true;
  }

  onCloseAddPersistentWatcher() {
    this.showAddPersistent = false;
    this.loadWatcherData();
  }

  showStatusTray(watcher) {
    this.targetPersistentWatcher = watcher;
    this.statusActions.primaryButtons[0].text = watcher.status === 'active' ? 'Pause' : 'Resume';
    this.showStatus = true;
  }

  async handleTogglePersistentStatus() {
    const newStatus = this.targetPersistentWatcher.status === 'active' ? 'paused' : 'active';
    try {
      await this.persistentWatcherService.updateWatcher(this.targetPersistentWatcher.id, {status: newStatus});
    } catch (err) {
      console.error(err);
      this.error = 'Failed to update watcher';
    } finally {
      this.onClosePopTray();
      this.loadWatcherData();
    }
  }

  showDeletePersistentTray(watcher) {
    this.targetPersistentWatcher = watcher;
    this.showDeletePersistent = true;
  }

  async handleDeletePersistentWatcher() {
    try {
      await this.persistentWatcherService.deleteWatcher(this.targetPersistentWatcher.id);
    } catch (err) {
      console.error(err);
      this.error = 'Failed to delete watcher';
    } finally {
      this.onClosePopTray();
      this.loadWatcherData();
    }
  }

  onClickWatcher(watcher): void {
    this.router.navigate(['/watcher', watcher.id]);
  }

  onClickPersistentWatcher(watcher): void {
    this.router.navigate(['/persistent-watcher', watcher.id]);
  }

  showDeleteTray(watcher) {
    this.targetFlightOption = watcher.flightOptionId;
    this.targetWatcher = watcher;
    this.showDelete = true;
  }

  async handleDeleteWatcher() {
    const update = {
      isActive: false,
      isVisible: false,
    }
    try {
      await this.watcherService.deleteFlightWatcher(this.targetWatcher.id);
      await this.flightOptionService.updateFlightOption(this.targetFlightOption, update);
    } catch (err) {
      console.error(err);
      this.error = 'Failed to delete watcher';
    } finally {
      this.onClosePopTray();
      this.loadWatcherData();
    }
  }

  showPrivacyTray(flightOption) {
    this.targetFlightOption = flightOption;
    this.showTogglePrivacy = true;
  }

  async handleTogglePrivacy(makePublic) {
    const update = {
      isVisible: makePublic,
    }
    try {
      await this.flightOptionService.updateFlightOption(this.targetFlightOption.id, update);
    } catch (err) {
      console.error(err);
      this.error = 'Failed to update watcher';
    } finally {
      this.onClosePopTray();
      this.loadWatcherData();
    }
  }

  onClosePopTray() {
    this.showDelete = false;
    this.showTogglePrivacy = false;
    this.showStatus = false;
    this.showDeletePersistent = false;
    this.targetWatcher = null;
    this.targetPersistentWatcher = null;
    this.targetFlightOption = null;
  }

}
